import { ApolloProvider } from '@apollo/client';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import React, { createContext, useContext } from 'react';

import { useAppApolloClient } from 'requests/apolloClient';

type ContextValue = {
  runInSandboxMode: boolean;
};

const SandboxModeContext = createContext<ContextValue>({
  runInSandboxMode: false,
});

export const useSandboxMode = () =>
  useContext<ContextValue>(SandboxModeContext);

// TODO(jon): override the axios client.

/** Causes all children to use sandbox mode. */
export const SandboxMode: React.FC = ({ children }) => {
  const apolloClient = useAppApolloClient({ executionMode: 'sandbox' });
  return (
    <SandboxModeContext.Provider value={{ runInSandboxMode: true }}>
      <ApolloProvider client={apolloClient}>{children}</ApolloProvider>
    </SandboxModeContext.Provider>
  );
};

/**
 * Causes all children to use live mode, unless overridden by {@link SandboxMode}.
 *
 * Note: use with caution, and only within a narrow scope!! Disabling sandbox mode in a component
 * hierarchy that expects to be operating in sandbox mode can cause data loss. This is best seen as
 * a temporary escape hatch - there is always a better long-term solution.
 *
 * In general, if it truly makes sense to exit sandbox mode for a specific request, it's better to
 * pass an explicit param to that endpoint only, rather than overriding sandbox mode for an entire
 * component hierarchy.
 */
export const UnsafeDisableSandboxMode: React.FC = withLDConsumer()(
  ({ children, flags }) => {
    const apolloClient = useAppApolloClient({
      executionMode: 'live',
      targetHeaderValue: flags?.targetHeaderValue,
    });
    return (
      <SandboxModeContext.Provider value={{ runInSandboxMode: false }}>
        <ApolloProvider client={apolloClient}>{children}</ApolloProvider>
      </SandboxModeContext.Provider>
    );
  },
);
